import { useCallback, useEffect, useRef, useState } from "react";

const secondsUntil = (deadline: number) => Math.max(0, Math.ceil((deadline - Date.now()) / 1000));

/**
 * Counts down to a deadline. Each tick recomputes from the clock, so a tick
 * that runs late (a backgrounded app, a throttled tab) lands on the right
 * value instead of losing the time it missed.
 */
export function useCountdown(seconds: number) {
  const [remaining, setRemaining] = useState(0);
  const deadline = useRef(0);
  const running = remaining > 0;

  // An interval rather than a timeout per second: a tick that lands on the
  // same value (the clock was set back) would otherwise stop the countdown
  useEffect(() => {
    if (!running) return;
    const interval = setInterval(() => setRemaining(secondsUntil(deadline.current)), 1000);
    return () => clearInterval(interval);
  }, [running]);

  const restart = useCallback(
    (duration: number = seconds) => {
      deadline.current = Date.now() + duration * 1000;
      setRemaining(duration);
    },
    [seconds],
  );

  return { remaining, restart };
}
